import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { queryClient, trpc, trpcErrorCode } from "../../app/trpc.js";
import { Button, Card, Chip, Field, Input, Notice, SectionTitle, Spinner } from "../../ui/index.js";
import { addDays, dayKey, fmtDay, fmtMoney, fmtTime, newKey } from "../cabinet/index.js";
import { PublicShell, usePublicLocale } from "./PublicShell.js";

/** Поле, к которому относится ошибка проверки (`details.field` у `validation`). */
const errorField = (e: unknown): unknown => (e as { data?: { details?: { field?: unknown } | null } } | null)?.data?.details?.field;

/**
 * Страница записи `/book/:slug` без переписки: для шапки Instagram, карточки Google и QR на зеркале.
 * Услуги → мастер или «любой» → день и время → имя и телефон → «моя запись». Запись сразу подтверждена
 * (решение 28.10.2026); на один телефон не больше двух активных записей (правило календаря).
 */
export function BookingPage() {
  const { t, i18n } = useTranslation();
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const salon = useQuery(trpc.onboarding.public.salon.queryOptions({ slug }, { retry: false }));
  usePublicLocale(salon.data?.locale);

  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [masterId, setMasterId] = useState<string>("any");
  const tz = salon.data?.timezone ?? "Europe/Belgrade";
  const today = useMemo(() => dayKey(new Date(), tz), [tz]);
  const [day, setDay] = useState<string | null>(null);
  const [slot, setSlot] = useState<{ start: Date; masterId: string } | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [key, setKey] = useState(newKey);
  const [error, setError] = useState<string | null>(null);

  const activeDay = day ?? today;
  const slots = useQuery(
    trpc.onboarding.public.slots.queryOptions(serviceIds.length > 0 ? { slug, serviceIds, masterId, day: activeDay } : skipToken, { staleTime: 30_000 }),
  );
  const book = useMutation(
    trpc.onboarding.public.book.mutationOptions({
      onSuccess: (r) => navigate(`/my/${r.manageToken}?new=1`),
      onError: (e) => {
        const code = trpcErrorCode(e);
        if (code === "slot_taken" || code === "outside_hours" || code === "master_unavailable") {
          setSlot(null);
          setKey(newKey());
          void queryClient.invalidateQueries(trpc.onboarding.public.slots.pathFilter());
          setError(t("book.slotTaken"));
        } else if (code === "booking_limit") setError(t("book.limit"));
        else if (code === "TOO_MANY_REQUESTS") setError(t("book.tooMany"));
        else if (code === "validation" && errorField(e) === "phone") setError(t("book.badPhone"));
        else if (code === "validation" || code === "not_found" || code === "BAD_REQUEST") {
          // Услугу выключили или мастера убрали, пока страница была открыта: телефон тут ни при чём.
          setSlot(null);
          setKey(newKey());
          void queryClient.invalidateQueries(trpc.onboarding.public.pathFilter());
          setError(t("book.changed"));
        } else if (code === "PRECONDITION_FAILED") setError(t("book.closed"));
        else setError(t("common.error"));
      },
    }),
  );

  if (salon.isPending) return <Spinner />;
  if (salon.isError || !salon.data)
    return (
      <PublicShell>
        <Notice kind="error">{trpcErrorCode(salon.error) === "TOO_MANY_REQUESTS" ? t("book.tooMany") : t("book.notFound")}</Notice>
      </PublicShell>
    );
  const s = salon.data;
  if (!s.bookable)
    return (
      <PublicShell salonName={s.name} slug={s.slug}>
        <h1 className="mb-2 text-2xl font-semibold text-ink">{s.name}</h1>
        <Notice>{s.reason === "closed" ? t("book.closed") : t("book.notOpen")}</Notice>
      </PublicShell>
    );

  const lang = i18n.language;
  const chosen = s.services.filter((x) => serviceIds.includes(x.id));
  const total = chosen.reduce((sum, x) => sum + x.price.amount, 0);
  const minutes = chosen.reduce((sum, x) => sum + x.durationMin, 0);
  const masters = s.masters.filter((m) => serviceIds.every((id) => m.serviceIds.includes(id)));
  const toggleService = (id: string) => {
    const next = serviceIds.includes(id) ? serviceIds.filter((x) => x !== id) : [...serviceIds, id];
    setServiceIds(next);
    setSlot(null);
    if (masterId !== "any" && !next.every((x) => s.masters.find((m) => m.id === masterId)?.serviceIds.includes(x))) setMasterId("any");
  };
  const pickMaster = (id: string) => {
    setMasterId(id);
    setSlot(null);
  };
  const pickDay = (d: string) => {
    setDay(d);
    setSlot(null);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!slot || !name.trim() || !phone.trim()) return;
    setError(null);
    book.mutate({ slug, serviceIds, masterId: slot.masterId, start: slot.start, name: name.trim(), phone: phone.trim(), idempotencyKey: key });
  };
  const days = Array.from({ length: s.horizonDays }, (_, i) => addDays(today, i));

  return (
    <PublicShell salonName={s.name} slug={s.slug}>
      <h1 className="text-2xl font-semibold text-ink">{t("book.title")}</h1>
      {s.address && <p className="mt-1 text-sm text-ink-3">{s.address}</p>}

      <SectionTitle>{t("book.services")}</SectionTitle>
      <div className="space-y-3">
        {s.services.map((x) => (
          <button key={x.id} type="button" onClick={() => toggleService(x.id)} className="block w-full text-left" aria-pressed={serviceIds.includes(x.id)}>
            <Card selected={serviceIds.includes(x.id)} className="flex items-center justify-between gap-3 py-3 transition hover:border-line-strong lg:py-3">
              <span className="min-w-0">
                <span className="block font-medium text-ink">{x.name}</span>
                <span className="block text-sm text-ink-3">{t("book.minutes", { count: x.durationMin })}</span>
              </span>
              <span className="shrink-0 font-semibold tabular-nums text-ink">{fmtMoney(x.price.amount, lang)}</span>
            </Card>
          </button>
        ))}
      </div>

      {serviceIds.length > 0 && (
        <>
          <SectionTitle>{t("book.master")}</SectionTitle>
          {masters.length === 0 ? (
            <Notice>{t("book.noMaster")}</Notice>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Chip active={masterId === "any"} onClick={() => pickMaster("any")}>
                {t("book.anyMaster")}
              </Chip>
              {masters.map((m) => (
                <Chip key={m.id} active={masterId === m.id} onClick={() => pickMaster(m.id)}>
                  {m.name}
                </Chip>
              ))}
            </div>
          )}

          <SectionTitle>{t("book.when")}</SectionTitle>
          {/* Лента дней во всю ширину карточки: на телефоне до краёв экрана, от `sm` до рамки карточки (`sm:p-6` у PublicShell). */}
          <fieldset className="no-scrollbar -mx-4 flex min-w-0 gap-2 overflow-x-auto px-4 pb-2 sm:-mx-6 sm:px-6" aria-label={t("book.when")}>
            {days.map((d) => (
              <Chip key={d} active={d === activeDay} onClick={() => pickDay(d)}>
                {fmtDay(d, lang)}
              </Chip>
            ))}
          </fieldset>
          {slots.isPending ? (
            <Spinner />
          ) : slots.isError ? (
            <Notice kind="error">{trpcErrorCode(slots.error) === "TOO_MANY_REQUESTS" ? t("book.tooMany") : t("book.slotsError")}</Notice>
          ) : (slots.data ?? []).length === 0 ? (
            <Notice>{t("book.noSlots")}</Notice>
          ) : (
            <div className="mt-2 flex flex-wrap gap-2">
              {(slots.data ?? []).map((x) => (
                <Chip key={x.start.toISOString()} active={slot?.start.getTime() === x.start.getTime()} onClick={() => setSlot(x)}>
                  {fmtTime(x.start, tz, lang)}
                </Chip>
              ))}
            </div>
          )}
        </>
      )}

      {slot && (
        <form onSubmit={onSubmit} className="mt-6">
          <Card className="space-y-4">
            <p className="font-medium text-ink">
              {t("book.summary", {
                services: chosen.map((x) => x.name).join(" + "),
                day: fmtDay(dayKey(slot.start, tz), lang, { weekday: "long", day: "numeric", month: "long" }),
                time: fmtTime(slot.start, tz, lang),
                master: masterId === "any" ? (s.masters.find((m) => m.id === slot.masterId)?.name ?? "") : (masters.find((m) => m.id === masterId)?.name ?? ""),
              })}
            </p>
            <p className="text-sm text-ink-3">{t("book.total", { total: fmtMoney(total, lang), minutes })}</p>
            <Field label={t("book.name")} htmlFor="book-name">
              <Input id="book-name" autoComplete="name" required maxLength={80} value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label={t("book.phone")} htmlFor="book-phone">
              <Input
                id="book-phone"
                type="tel"
                autoComplete="tel"
                required
                inputMode="tel"
                placeholder="064 123 4567"
                maxLength={30}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Field>
            <p className="text-xs text-ink-3">{t("book.consent")}</p>
            {error && <Notice kind="error">{error}</Notice>}
            <Button type="submit" className="w-full" size="lg" disabled={book.isPending || !name.trim() || !phone.trim()}>
              {book.isPending ? t("common.loading") : t("book.submit")}
            </Button>
            <p className="text-xs text-ink-3">{t("book.noPrepayment")}</p>
          </Card>
        </form>
      )}
    </PublicShell>
  );
}
